import { FC, useState, useEffect, useCallback } from 'react';
import { Wrench, Home, Store, DollarSign, RefreshCw, MapPin, Droplets, Volume2, ImageOff, Calendar, Pencil, X, RotateCcw, ExternalLink } from 'lucide-react';
import { Property, PropertyStatus } from '@/types/index';
import { calcDiffMarche } from '@utils/property-calcs';
import { computeFlipStatus } from '@/utils/flipViability';

const API_URL = import.meta.env.VITE_API_URL ?? '';

const STATUS_LABELS: Partial<Record<PropertyStatus, string>> = {
  [PropertyStatus.FIRST_SELECTION]: 'Sélectionné',
  [PropertyStatus.OPPORTUNITY]: 'Opportunité',
  [PropertyStatus.UNDER_ANALYSIS]: 'En analyse',
  [PropertyStatus.PURCHASED]: 'Acheté',
};

const STATUS_COLORS: Partial<Record<PropertyStatus, string>> = {
  [PropertyStatus.FIRST_SELECTION]: 'bg-blue-600 text-white',
  [PropertyStatus.OPPORTUNITY]: 'bg-emerald-600 text-white',
  [PropertyStatus.UNDER_ANALYSIS]: 'bg-amber-500 text-white',
  [PropertyStatus.PURCHASED]: 'bg-purple-600 text-white',
};

const fmtNum = (v: number) =>
  v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`;

const fmtPct = (v?: number | null) =>
  v != null ? `${v.toFixed(1)} %` : null;

const popTrendBadge = (curr?: number | null, prev?: number | null) => {
  if (!curr || !prev || curr === prev)
    return { arrow: null, cls: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400' };
  return curr > prev
    ? { arrow: '↗', cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' }
    : { arrow: '↘', cls: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' };
};

const chomTrendBadge = (curr?: number | null, prev?: number | null) => {
  if (!curr) return { arrow: null, cls: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400' };
  if (prev && Math.abs(curr - prev) >= 0.05) {
    return curr < prev
      ? { arrow: '↘', cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' }
      : { arrow: '↗', cls: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' };
  }
  // Fallback: absolute thresholds when no prev data
  if (curr < 3.5) return { arrow: '↘', cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' };
  if (curr > 5.0) return { arrow: '↗', cls: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' };
  return { arrow: '→', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400' };
};

interface Props {
  property: Property;
  onReject?: (p: Property) => void;
  onRestore?: (p: Property) => void;
  onEdit?: (p: Property) => void;
  onPromote?: (p: Property) => void;
}

export const PropertyCard: FC<Props> = ({ property, onReject, onRestore, onEdit, onPromote }) => {
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const photos = (property.photos ?? []).map(p => `${API_URL}${p.url}`);
  const hasMany = photos.length > 1;

  const prev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPhotoIdx(i => (i - 1 + photos.length) % photos.length);
  }, [photos.length]);

  const next = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPhotoIdx(i => (i + 1) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, prev, next]);

  const isNew =
    property.firstSeenAt &&
    Date.now() - new Date(property.firstSeenAt).getTime() < 7 * 24 * 3600 * 1000;

  const yieldVal = property.estimatedYield ?? property.yieldRate;
  const commune = property.commune;
  const isOpportunity = property.status === PropertyStatus.OPPORTUNITY;

  const diffMarche = calcDiffMarche(property.askingPrice, property.estimatedSalePrice);

  const cashFlowMonthly = property.cashFlowMonthly ?? null;

  return (
    <article className="relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-row">

      {/* Photo / Carousel */}
      <div className="relative w-28 sm:w-36 md:w-44 flex-shrink-0 bg-gray-100 dark:bg-gray-700 self-stretch group">
        {photos.length > 0 ? (
          <>
            <img
              src={photos[photoIdx]}
              alt={`${property.city} — ${property.area} m²`}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => setLightboxOpen(true)}
              loading="lazy"
            />
            {hasMany && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-0.5 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white rounded-full w-9 h-9 sm:w-6 sm:h-6 flex items-center justify-center text-lg sm:text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Photo précédente"
                >‹</button>
                <button
                  onClick={next}
                  className="absolute right-0.5 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white rounded-full w-9 h-9 sm:w-6 sm:h-6 flex items-center justify-center text-lg sm:text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Photo suivante"
                >›</button>
                <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full leading-none">
                  {photoIdx + 1}/{photos.length}
                </span>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full min-h-[110px] flex items-center justify-center text-gray-300 dark:text-gray-600">
            <ImageOff className="w-8 h-8" />
          </div>
        )}
      </div>

      {/* Contenu */}
      <div className="flex-1 p-2 sm:p-3 flex flex-col gap-1.5 min-w-0">

        {/* Ligne 1 : badges + icônes flags + source */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isNew && (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-500 text-white">
                Nouveau
              </span>
            )}
            {property.status && STATUS_LABELS[property.status] && (
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[property.status] ?? 'bg-gray-500 text-white'}`}>
                {STATUS_LABELS[property.status]}
              </span>
            )}
            {/* Icônes flags LLM — n'affiche que si analysé et flag actif */}
            <div className="flex items-center gap-1">
              {(() => {
                const fs = computeFlipStatus(property);
                if (fs === null) return null;
                const cfg = {
                  green:  { bg: 'bg-emerald-100 dark:bg-emerald-900/30', dot: 'bg-emerald-500', title: 'Flip viable au prix affiché' },
                  orange: { bg: 'bg-amber-100 dark:bg-amber-900/30',     dot: 'bg-amber-400',   title: 'Négociation flip nécessaire (écart ≤ 20%)' },
                  red:    { bg: 'bg-red-100 dark:bg-red-900/30',         dot: 'bg-red-500',     title: 'Flip irréaliste au prix affiché' },
                }[fs];
                return (
                  <span title={cfg.title} className={`w-6 h-6 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                  </span>
                );
              })()}
            {(property.descriptionAnalyzedAt || property.photoAnalyzedAt) && (<>
                {property.renovationLevel === 'major' && (
                  <span title="Rénovation lourde" className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0 text-[11px]">
                    🔨
                  </span>
                )}
                {property.renovationLevel === 'minor' && (
                  <span title="Travaux mineurs" className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-3.5 h-3.5 text-amber-500" />
                  </span>
                )}
                {property.renovationLevel === 'none' && (
                  <span title="Bon état" className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 text-[11px]">
                    ✓
                  </span>
                )}
              </>)}
            {property.descriptionAnalyzedAt && (<>
                {property.isRented === true && (
                  <span title="Locataire en place" className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <Home className="w-3.5 h-3.5 text-amber-500" />
                  </span>
                )}
                {property.isMixedUse === true && (
                  <span title="Usage mixte / local commercial" className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                    <Store className="w-3.5 h-3.5 text-purple-500" />
                  </span>
                )}
                {property.isStrategyLocatif === true && (
                  <span title="Stratégie locative" className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-3.5 h-3.5 text-blue-500" />
                  </span>
                )}
                {property.isStrategyFlip === true && (
                  <span title="Stratégie flip (rénovation / revente)" className="w-6 h-6 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="w-3.5 h-3.5 text-rose-500" />
                  </span>
                )}
                {property.isAddressComplete === false && (
                  <span title="Adresse incomplète" className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-3.5 h-3.5 text-red-500" />
                  </span>
                )}
              </>)}
              {(property.floodHazard === 'high' || property.floodHazard === 'medium') && (
                <span
                  title={`Risque inondation ${property.floodHazard === 'high' ? 'élevé' : 'modéré'}`}
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${property.floodHazard === 'high' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-orange-100 dark:bg-orange-900/30'}`}
                >
                  <Droplets className={`w-3.5 h-3.5 ${property.floodHazard === 'high' ? 'text-red-500' : 'text-orange-500'}`} />
                </span>
              )}
              {((property.roadNoiseDb != null && property.roadNoiseDb >= 60) || (property.railNoiseDb != null && property.railNoiseDb >= 60)) && (
                <span
                  title={[
                    property.roadNoiseDb != null ? `Route: ${property.roadNoiseDb} dB` : null,
                    property.railNoiseDb != null ? `Rail: ${property.railNoiseDb} dB` : null,
                  ].filter(Boolean).join(' | ')}
                  className="w-6 h-6 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center flex-shrink-0"
                >
                  <Volume2 className="w-3.5 h-3.5 text-yellow-600" />
                </span>
              )}
            </div>
          </div>
          {property.sourceName && (
            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap max-w-[80px] sm:max-w-none truncate">
              {property.sourceName}
            </span>
          )}
        </div>

        {/* Corps : 3 zones */}
        <div className="flex gap-1.5 sm:gap-2 flex-1 min-w-0">

          {/* Zone gauche grise : infos bien */}
          <div className="bg-slate-50 dark:bg-gray-700/40 rounded-lg px-2 sm:px-2.5 py-2 flex flex-col gap-0.5 shrink-0">
            <p className="text-sm sm:text-base font-bold text-blue-700 dark:text-blue-400 leading-tight">
              {property.askingPrice != null ? `${property.askingPrice.toLocaleString('fr-CH')} CHF` : '—'}
            </p>
            {property.pricePerM2 != null && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {Math.round(property.pricePerM2).toLocaleString('fr-CH')} CHF/m²
              </p>
            )}
            {(property.city || property.canton) && (
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                {property.city}{property.canton ? ` · ${property.canton}` : ''}
              </p>
            )}
            {(property.area || property.rooms) && (
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {[
                  property.area ? `${property.area} m²` : null,
                  property.rooms ? `${property.rooms} pièces` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            )}
            {property.firstSeenAt && (
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                <Calendar className="w-3 h-3 flex-shrink-0" />
                {new Date(property.firstSeenAt).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </p>
            )}
          </div>

          {/* Zone centre blanche : grille 2×2 */}
          <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-1 sm:gap-x-3 gap-y-1.5 sm:gap-y-2 content-center">
            {/* Loyer estimé */}
            <div className="min-w-0 overflow-hidden">
              <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 truncate">Loyer estimé</p>
              <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                {property.estimatedRent != null ? `${Math.round(property.estimatedRent).toLocaleString('fr-CH')} CHF/mois` : '—'}
              </p>
            </div>

            {/* Diff. marché */}
            <div className="min-w-0 overflow-hidden">
              {diffMarche != null ? (
                <>
                  <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 truncate">Diff. marché</p>
                  <p className={`text-xs sm:text-sm font-semibold truncate ${diffMarche < -10 ? 'text-emerald-600 dark:text-emerald-400' : diffMarche > 10 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {diffMarche < -10 ? '▼' : diffMarche > 10 ? '▲' : '●'} {diffMarche > 0 ? '+' : ''}{diffMarche.toFixed(0)} %
                  </p>
                </>
              ) : <div />}
            </div>

            {/* Score opportunité */}
            <div className="min-w-0 overflow-hidden">
              {property.opportunityScore != null ? (
                <>
                  <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 truncate">Score</p>
                  <p className={`text-xs sm:text-sm font-bold truncate ${
                    property.opportunityScore >= 70 ? 'text-emerald-600 dark:text-emerald-400' :
                    property.opportunityScore >= 45 ? 'text-amber-500 dark:text-amber-400' :
                    'text-red-500 dark:text-red-400'
                  }`}>
                    {property.opportunityScore}<span className="text-[9px] font-normal text-gray-400 dark:text-gray-500">/100</span>
                  </p>
                </>
              ) : <div />}
            </div>

            {/* Badge population */}
            <div className="flex items-center min-w-0 overflow-hidden">
              {commune?.population && (() => {
                const t = popTrendBadge(commune.population, commune.populationPrev);
                return (
                  <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium truncate ${t.cls}`}>
                    {t.arrow && <span>{t.arrow}</span>}
                    {fmtNum(commune.population)} hab.
                  </span>
                );
              })()}
            </div>

            {/* Badge chômage */}
            <div className="flex items-center min-w-0 overflow-hidden">
              {commune?.unemploymentRate && (() => {
                const t = chomTrendBadge(commune.unemploymentRate, commune.unemploymentRatePrev);
                return (
                  <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium truncate ${t.cls}`}>
                    {t.arrow && <span>{t.arrow}</span>}
                    {commune.unemploymentRate.toFixed(1)} % chôm.
                  </span>
                );
              })()}
            </div>
          </div>

          {/* Zone droite grise : cash flow + rendement */}
          <div className="bg-slate-50 dark:bg-gray-700/40 rounded-lg px-2 sm:px-2.5 py-2 flex flex-col gap-1.5 sm:gap-2 justify-center shrink-0">
            {cashFlowMonthly != null && (
              <div>
                <p className="text-xs text-gray-400 dark:text-gray-500">Cash flow</p>
                <p className={`text-sm font-semibold ${cashFlowMonthly >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                  {cashFlowMonthly.toLocaleString('fr-CH')} /mois
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500">Rendement brut</p>
              <p className={`text-sm font-semibold ${
                yieldVal != null
                  ? yieldVal >= 5 ? 'text-emerald-600 dark:text-emerald-400'
                    : yieldVal >= 3 ? 'text-amber-600 dark:text-amber-400'
                    : 'text-red-500 dark:text-red-400'
                  : 'text-gray-400'
              }`}>
                {fmtPct(yieldVal) ?? '—'}
              </p>
            </div>
          </div>

        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 pt-0.5">
          <a
            href={`/analysis/${property.id}`}
            className="px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold transition-colors"
          >
            Analyser
          </a>
          {onEdit && (
            <button onClick={() => onEdit(property)} title="Modifier" aria-label="Modifier"
              className="p-2.5 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-blue-600 hover:border-blue-300 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
              <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
          )}
          {onReject && property.status !== PropertyStatus.REJECTED && (
            <button onClick={() => onReject(property)} title="Rejeter" aria-label="Rejeter"
              className="p-2.5 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <X className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
          )}
          {onRestore && property.status === PropertyStatus.REJECTED && (
            <button onClick={() => onRestore(property)} title="Restaurer" aria-label="Restaurer"
              className="p-2.5 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-emerald-500 hover:border-emerald-300 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors">
              <RotateCcw className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </button>
          )}
          {property.sourceUrl && (
            <a href={property.sourceUrl} target="_blank" rel="noopener noreferrer" title="Voir l'annonce originale" aria-label="Voir l'annonce originale"
              className="p-2.5 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-400 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <ExternalLink className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            </a>
          )}
        </div>
      </div>

      {/* Bouton cœur — Opportunité */}
      {onPromote && (
        <button
          onClick={e => { e.stopPropagation(); onPromote(property); }}
          title={isOpportunity ? 'Retirer des opportunités' : 'Ajouter aux opportunités'}
          className="absolute bottom-2.5 right-2.5 p-1 rounded-full transition-transform hover:scale-110"
        >
          {isOpportunity ? (
            <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          )}
        </button>
      )}

      {/* Lightbox plein écran */}
      {lightboxOpen && photos.length > 0 && (
        <div
          className="fixed inset-0 z-[9999] bg-black/92 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Fermer */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/25 rounded-full w-10 h-10 flex items-center justify-center text-xl transition-colors"
            aria-label="Fermer"
          >✕</button>

          {/* Compteur */}
          {hasMany && (
            <span className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
              {photoIdx + 1} / {photos.length}
            </span>
          )}

          {/* Flèche gauche */}
          {hasMany && (
            <button
              onClick={e => { e.stopPropagation(); prev(); }}
              className="absolute left-4 text-white bg-white/10 hover:bg-white/25 rounded-full w-12 h-12 flex items-center justify-center text-2xl transition-colors"
              aria-label="Photo précédente"
            >‹</button>
          )}

          {/* Image */}
          <img
            src={photos[photoIdx]}
            alt={`${property.city} ${photoIdx + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain select-none rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
            draggable={false}
          />

          {/* Flèche droite */}
          {hasMany && (
            <button
              onClick={e => { e.stopPropagation(); next(); }}
              className="absolute right-4 text-white bg-white/10 hover:bg-white/25 rounded-full w-12 h-12 flex items-center justify-center text-2xl transition-colors"
              aria-label="Photo suivante"
            >›</button>
          )}

          {/* Miniatures */}
          {hasMany && (
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 px-3 py-2 bg-black/50 rounded-xl max-w-[90vw] overflow-x-auto"
              onClick={e => e.stopPropagation()}
            >
              {photos.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setPhotoIdx(i)}
                  className={`flex-shrink-0 w-14 h-10 rounded overflow-hidden border-2 transition-colors ${
                    i === photoIdx ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
};
